import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, MapPin, Calendar, Tag, Pencil, Ban, CheckCircle2, RotateCcw, Check, Share2, Trash2, Bookmark, BookmarkCheck } from "lucide-react";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { MemberLink } from "@/components/build/MemberLink";
import { ConfirmDialog } from "./ConfirmDialog";
import { ApplyToSlotModal } from "./ApplyToSlotModal";
import { CollaborationApplicationsPanel } from "./CollaborationApplicationsPanel";
import { CreateCollaborationModal } from "./CreateCollaborationModal";
import { CreateNjmCollaborationModal } from "./CreateNjmCollaborationModal";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { ActivityTrail } from "@/components/ActivityTrail";
import { useNotes } from "@/components/notes/NotesHub";

const STATUS_CHIP = {
  pending: { label: "Applied · Pending", cls: "bg-brand/20 text-brand-dim" },
  shortlisted: { label: "Shortlisted", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "You're in!", cls: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Not selected", cls: "bg-red-100 text-red-600" },
  withdrawn: { label: "Withdrawn", cls: "bg-hairline text-faint" },
};
const eyebrow = "font-mont text-[9.5px] font-extrabold uppercase tracking-[0.15em] text-faint";
const DISCLAIMER_TEXT = "Payment is arranged directly by the Member Collaboration's creator. NJM is not a party to the payment.";
const formatFee = (amount) => `₹${Number(amount).toLocaleString("en-IN")}`;

/**
 * Collaboration Opportunities, Phase 2 (2026-08) — the detail view opened from a Floor card's
 * "View Opportunity". Visual pass (2026-08) matches the approved mockup: a mini project page
 * with THE TEAM as the visual centre (filled seats read ✓, open seats carry an inline Apply
 * button). Same ONE component for every viewer — role-conditional rendering, never a separate
 * creator page: `opp.is_creator` unlocks the Manage/Review sections, `opp.my_applications`
 * renders the viewer's own application status per slot, everyone else just sees the derived
 * seat counts + accepted members. This pass is visual only — every endpoint call, guard and
 * state transition below is unchanged from the working Phase 2 build.
 */
export function CollaborationDetailModal({ opportunityId, viewerIsFreelancer, onClose, onChanged, autoOpenApplications = false }) {
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applySlot, setApplySlot] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showApplications, setShowApplications] = useState(autoOpenApplications);
  const [busyAction, setBusyAction] = useState(false);
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [adminBudget, setAdminBudget] = useState(null);
  const { openSendNote } = useNotes();

  const load = () => {
    api.get(`/v1/collaboration/opportunities/${opportunityId}`)
      .then((r) => setOpp(r.data.opportunity))
      .catch(() => setOpp(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Only the creator can ever see the Edit button, but fetching these two directories is
    // cheap and avoids a round-trip the moment they click it.
    api.get("/v1/build/roles").then((r) => setRoleOptions(r.data.items || [])).catch(() => {});
    api.get("/v1/build/locations").then((r) => setCityOptions(r.data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  // Wave 3 (2026-09-30) — staff-only budget block, deliberately fetched from its OWN endpoint
  // (never part of the freelancer-facing `opp` payload) so it can never leak by accident.
  // Dashboard Fixes (2026-11), Item 1 — opening an NJM Collaboration's manage view (this is
  // it — `opp.can_manage` only ever true for staff here) is exactly the "PM opened the
  // Collaboration" signal that clears its Dashboard "Collaboration Activity" item.
  useEffect(() => {
    if (opp && opp.kind === "njm" && opp.can_manage) {
      api.get(`/v1/collaboration/opportunities/${opportunityId}/admin-budget`).then((r) => setAdminBudget(r.data)).catch(() => setAdminBudget(null));
      api.post(`/v1/collaboration/opportunities/${opportunityId}/mark-read`).catch(() => {});
    } else {
      setAdminBudget(null);
    }
  }, [opp && opp.kind, opp && opp.can_manage, opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const myApplicationForSlot = (slotId) => {
    const apps = (opp.my_applications || []).filter((a) => a.slot_id === slotId);
    if (apps.length === 0) return null;
    return apps.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  };

  const setStatus = async (status) => {
    setBusyAction(true);
    try {
      await api.post(`/v1/collaboration/opportunities/${opp.id}/status`, { status });
      toast.success(status === "open" ? "Reopened" : status === "closed" ? "Marked as closed" : "Cancelled");
      load();
      onChanged?.();
    } catch {
      toast.error("Couldn't update status");
    } finally {
      setBusyAction(false);
    }
  };

  // Amendment 9 (2026-09-30): hard-delete if no applications exist, soft-delete (hidden,
  // history preserved) otherwise — the server decides which, this just surfaces the result.
  const deleteOpportunity = async () => {
    setDeleting(true);
    const label = opp.kind === "njm" ? "NJM Collaboration" : "Member Collaboration";
    try {
      const r = await api.delete(`/v1/collaboration/opportunities/${opp.id}`);
      toast.success(r.data.hard_deleted ? `${label} deleted` : `${label} deleted — applicants' history is preserved`);
      onChanged?.();
      onClose();
    } catch {
      toast.error(`Couldn't delete this ${label.toLowerCase()}`);
    } finally {
      setDeleting(false);
    }
  };

  const withdraw = async (applicationId) => {
    setWithdrawingId(applicationId);
    try {
      await api.delete(`/v1/collaboration/applications/${applicationId}`);
      toast.success("Application withdrawn");
      load();
      onChanged?.();
    } catch {
      toast.error("Couldn't withdraw");
    } finally {
      setWithdrawingId(null);
    }
  };

  const totalTarget = opp ? 1 + opp.role_slots.reduce((s, r) => s + r.quantity_required, 0) : 0;
  const totalFilled = opp ? 1 + opp.role_slots.reduce((s, r) => s + r.quantity_filled, 0) : 0;
  const teamComplete = opp && opp.status === "open" && totalFilled >= totalTarget;

  // Wave 3 (2026-09) — Share: copies the stable, addressable deep link (/community/
  // collaboration/:id — see pages/CollaborationDeepLink.jsx) for THIS specific Collaboration.
  // Copy-to-clipboard only for V1, per the brief — no native share sheet exists elsewhere in
  // this app for a comparable case, so nothing to reuse there.
  const shareLink = async () => {
    const url = `${window.location.origin}/community/collaboration/${opp.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  // Wave 2 (Nikhil, 2026-09-30) — save/bookmark, same UX as External saves.
  const toggleSave = async () => {
    setSavingBookmark(true);
    try {
      if (opp.is_saved) await api.delete(`/v1/collaboration/opportunities/${opp.id}/save`);
      else await api.post(`/v1/collaboration/opportunities/${opp.id}/save`);
      setOpp((prev) => ({ ...prev, is_saved: !prev.is_saved }));
    } catch {
      toast.error("Couldn't update saved status");
    } finally {
      setSavingBookmark(false);
    }
  };

  const showFeeDisclaimer = opp && opp.kind === "member" && opp.compensation === "paid";
  const canManage = opp && (opp.is_creator || (opp.kind === "njm" && opp.can_manage));
  const formatBudget = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose} data-testid="collaboration-detail-overlay">
      <div className="w-full max-w-xl rounded-2xl bg-paper shadow-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-faint" /></div>
        ) : !opp ? (
          <div className="p-8 text-center">
            <div className="text-sm text-faint" data-testid="collaboration-detail-not-found">This opportunity isn't available anymore.</div>
            <button onClick={onClose} className="mt-3 font-mont text-xs font-semibold text-ink hover:underline">Close</button>
          </div>
        ) : (
          <>
            <div className={`shrink-0 border-t-[3px] px-6 sm:px-7 py-5 ${opp.status === "cancelled" ? "bg-ink border-red-400" : teamComplete ? "bg-ink border-ink" : "bg-ink border-brand"}`}>
              <div className="flex items-start justify-between">
                <span className={`font-mont text-[9.5px] font-extrabold uppercase tracking-[0.15em] ${opp.status === "cancelled" ? "text-red-400" : teamComplete ? "text-paper" : "text-brand"}`}>
                  {opp.status === "cancelled" ? "Cancelled" : opp.kind === "njm" ? "NJM Collaboration" : "Member Collaboration"}
                </span>
                <div className="flex items-center gap-3">
                  {viewerIsFreelancer && !opp.is_creator && (
                    <button onClick={toggleSave} disabled={savingBookmark} data-testid="collaboration-detail-save-btn" title={opp.is_saved ? "Saved" : "Save"}
                      className="inline-flex items-center gap-1.5 text-paper/70 hover:text-paper transition-colors font-mont text-[10.5px] font-semibold uppercase tracking-wider disabled:opacity-50">
                      {opp.is_saved ? <BookmarkCheck className="h-3.5 w-3.5 fill-brand text-brand" /> : <Bookmark className="h-3.5 w-3.5" />} {opp.is_saved ? "Saved" : "Save"}
                    </button>
                  )}
                  <button onClick={shareLink} data-testid="collaboration-detail-share-btn" title="Copy link"
                    className="inline-flex items-center gap-1.5 text-paper/70 hover:text-paper transition-colors font-mont text-[10.5px] font-semibold uppercase tracking-wider">
                    <Share2 className="h-3.5 w-3.5" /> Share
                  </button>
                  <button onClick={onClose} data-testid="collaboration-detail-close" className="text-paper/60 hover:text-paper transition-colors text-lg leading-none">✕</button>
                </div>
              </div>
              <h2 className="mt-2.5 font-mont text-xl sm:text-2xl font-bold leading-tight text-paper" data-testid="collaboration-detail-project-name">{opp.project_name}</h2>
              {teamComplete && (
                <span className="mt-2.5 inline-block rounded bg-paper px-2 py-1 font-mont text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-emerald-700">Team complete ✓</span>
              )}
              <div className="mt-3 flex items-center gap-2">
                <ProfileAvatar freelancerId={opp.creator_freelancer_id} hasPhoto={Boolean(opp.creator_photo)} alt=""
                  className="h-6 w-6 rounded-full object-cover shrink-0"
                  fallback={<div className="h-6 w-6 rounded-full bg-brand text-ink flex items-center justify-center font-mont text-[9px] font-bold shrink-0">{(opp.creator_name || "?")[0]}</div>} />
                <span className="font-mont text-[10.5px] text-paper/75">Created by <MemberLink freelancerId={opp.creator_freelancer_id} className="font-bold text-paper hover:underline" testId="collaboration-detail-creator-link">{opp.creator_name}</MemberLink> · {opp.creator_role}</span>
              </div>
              <div className="mt-2 flex items-center gap-3 flex-wrap font-mont text-[10.5px] text-paper/55">
                {(opp.city || opp.location_type) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[opp.city, opp.location_type].filter(Boolean).join(" · ")}</span>}
                {opp.timeline && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {opp.timeline}</span>}
                {opp.project_type && <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {opp.project_type}</span>}
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-6 sm:px-7 py-5 space-y-6" data-testid="collaboration-detail-content">
              <div>
                <div className={eyebrow}>About</div>
                <p className="mt-1.5 font-mont text-[13px] leading-relaxed text-ink-soft whitespace-pre-wrap" data-testid="collaboration-detail-description">{opp.description}</p>
              </div>

              <div data-testid="collaboration-detail-team">
                <div className="flex items-center justify-between">
                  <div className={eyebrow}>The Team</div>
                  <span className="font-mont text-[10px] font-bold text-ink">{totalFilled} of {totalTarget} on board</span>
                </div>
                <div className="mt-2 h-1 rounded-full bg-black/8 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${teamComplete ? "bg-ink" : "bg-brand"}`} style={{ width: `${Math.min(100, (totalFilled / totalTarget) * 100)}%` }} />
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-3 rounded-xl border border-black/10 bg-[#FFFCF2] p-3" data-testid="collaboration-detail-creator">
                    <ProfileAvatar freelancerId={opp.creator_freelancer_id} hasPhoto={Boolean(opp.creator_photo)} alt=""
                      className="h-8 w-8 rounded-full object-cover shrink-0"
                      fallback={<div className="h-8 w-8 rounded-full bg-brand text-ink flex items-center justify-center font-mont text-xs font-bold shrink-0">{(opp.creator_name || "?")[0]}</div>} />
                    <div className="flex-1 min-w-0">
                      <MemberLink freelancerId={opp.creator_freelancer_id} className="font-mont text-[12.5px] font-bold text-ink hover:underline" testId="collaboration-detail-creator-link-2">{opp.creator_name}</MemberLink>
                      <div className="font-mont text-[10px] text-faint">{opp.creator_role}</div>
                    </div>
                    <span className="rounded bg-ink px-1.5 py-1 font-mont text-[7.5px] font-extrabold uppercase tracking-[0.12em] text-brand shrink-0">Creator</span>
                  </div>

                  {/* MESSAGE CREATOR — only for an ACCEPTED applicant (server-provided
                      my_applications status), never the creator, never pending/declined. Opens
                      the SAME shared modal + pair thread; the collaboration ref is metadata only. */}
                  {!opp.is_creator && openSendNote && opp.creator_freelancer_id
                    && (opp.my_applications || []).some((a) => a.status === "accepted") && (
                    <button onClick={() => openSendNote({ id: opp.creator_freelancer_id, name: opp.creator_name, profile_image: opp.creator_photo, primary_role: opp.creator_role }, { source: "collaboration", context: { type: "collaboration", ref_id: opp.id } })}
                      data-testid="collaboration-detail-message-creator"
                      className="w-full rounded-lg border border-ink/15 bg-paper py-2.5 font-mont text-[11px] font-bold text-ink hover:border-brand hover:bg-brand/5 transition-colors">
                      Message creator
                    </button>
                  )}

                  {opp.role_slots.map((s) => {
                    const mine = myApplicationForSlot(s.slot_id);
                    const openSeats = Math.max(0, s.quantity_required - s.quantity_filled);
                    const canApply = viewerIsFreelancer && !opp.is_creator && opp.status === "open" && s.status === "open" && (!mine || ["declined", "withdrawn"].includes(mine.status));
                    const showMineStatus = mine && !canApply;
                    return (
                      <div key={s.slot_id} className="space-y-2" data-testid={`collaboration-detail-slot-${s.slot_id}`}>
                        {(s.accepted_members || []).map((m, idx) => (
                          <div key={`${s.slot_id}-${m.freelancer_id || idx}`} className="flex items-center gap-3 rounded-xl border border-black/10 p-3">
                            <ProfileAvatar freelancerId={m.freelancer_id} hasPhoto={Boolean(m.photo)} alt=""
                              className="h-8 w-8 rounded-full object-cover shrink-0"
                              fallback={<div className="h-8 w-8 rounded-full bg-ink text-brand flex items-center justify-center font-mont text-xs font-bold shrink-0">{(m.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2)}</div>} />
                            <div className="flex-1 min-w-0">
                              <MemberLink freelancerId={m.freelancer_id} className="font-mont text-[12.5px] font-bold text-ink hover:underline" testId={`collaboration-detail-accepted-${s.slot_id}-${idx}`}>{m.name}</MemberLink>
                              <div className="font-mont text-[10px] text-faint">{s.role}</div>
                            </div>
                            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                          </div>
                        ))}
                        {openSeats > 0 && (
                          <div className="rounded-xl border border-black/10 border-l-[3px] border-l-brand p-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full border-[1.5px] border-dashed border-black/25 flex items-center justify-center shrink-0 font-mont text-faint text-sm">+</div>
                              <div className="flex-1 min-w-0">
                                <div className="font-mont text-[12.5px] font-bold text-ink">{s.role}</div>
                                <div className="font-mont text-[10px] font-semibold text-[#8A6A00]">
                                  {[s.expertise_niche, `${openSeats} spot${openSeats === 1 ? "" : "s"} open`].filter(Boolean).join(" · ")}
                                </div>
                                {(s.fee_amount || s.fee_tbd) && (
                                  <div className="mt-0.5 font-mont text-[10px] font-bold text-emerald-700" data-testid={`collaboration-detail-fee-${s.slot_id}`}>
                                    {s.fee_tbd ? "Fee to be discussed" : formatFee(s.fee_amount)}
                                  </div>
                                )}
                              </div>
                            </div>
                            {canApply && (
                              <button onClick={() => setApplySlot(s)} data-testid={`collaboration-detail-apply-${s.slot_id}`}
                                className="mt-2.5 w-full rounded-lg bg-ink py-2.5 font-mont text-[11px] font-bold text-paper hover:bg-ink/85 transition-colors">
                                Apply to collaborate →
                              </button>
                            )}
                          </div>
                        )}
                        {showMineStatus && (
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-black/8 bg-surface2/30 px-3 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mont text-[10px] font-medium ${STATUS_CHIP[mine.status]?.cls || "bg-hairline text-faint"}`} data-testid={`collaboration-detail-my-status-${s.slot_id}`}>
                              {STATUS_CHIP[mine.status]?.label || mine.status} · {s.role}
                            </span>
                            {["pending", "shortlisted", "accepted"].includes(mine.status) && (
                              <button onClick={() => withdraw(mine.id)} disabled={withdrawingId === mine.id} data-testid={`collaboration-detail-withdraw-${s.slot_id}`}
                                className="font-mont text-[10px] text-faint hover:text-red-500 underline disabled:opacity-50">Withdraw</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {opp.inspiration_links?.length > 0 && (
                <div data-testid="collaboration-detail-links">
                  <div className={eyebrow}>References</div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {opp.inspiration_links.map((l, i) => <LinkPreviewCard key={i} url={l} />)}
                  </div>
                </div>
              )}

              {opp.additional_notes && (
                <div data-testid="collaboration-detail-notes">
                  <div className={eyebrow}>Notes</div>
                  <p className="mt-1.5 font-mont text-[12.5px] text-ink-soft leading-relaxed">{opp.additional_notes}</p>
                </div>
              )}

              {canManage && (
                <div className="rounded-xl border border-brand/30 bg-brand/5 p-4 space-y-3" data-testid="collaboration-detail-manage">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className={eyebrow}>Manage this opportunity</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {opp.status !== "cancelled" && (
                        <button onClick={() => setShowEdit(true)} data-testid="collaboration-detail-edit-btn"
                          className="inline-flex items-center gap-1 rounded-lg border border-black/12 px-2.5 py-1.5 font-mont text-[11px] font-semibold text-ink-soft hover:border-ink/40">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                      )}
                      {opp.status === "open" && (
                        <button onClick={() => setStatus("closed")} disabled={busyAction} data-testid="collaboration-detail-close-btn"
                          className="inline-flex items-center gap-1 rounded-lg border border-black/12 px-2.5 py-1.5 font-mont text-[11px] font-semibold text-ink-soft hover:border-ink/40 disabled:opacity-50">
                          <CheckCircle2 className="h-3 w-3" /> Close
                        </button>
                      )}
                      {opp.status === "closed" && (
                        <button onClick={() => setStatus("open")} disabled={busyAction} data-testid="collaboration-detail-reopen-btn"
                          className="inline-flex items-center gap-1 rounded-lg border border-black/12 px-2.5 py-1.5 font-mont text-[11px] font-semibold text-ink-soft hover:border-ink/40 disabled:opacity-50">
                          <RotateCcw className="h-3 w-3" /> Reopen
                        </button>
                      )}
                      {opp.status !== "cancelled" && (
                        <ConfirmDialog
                          testIdPrefix="collaboration-detail-cancel"
                          title="Cancel this opportunity?"
                          description="Applicants (pending and accepted) will be notified this project was cancelled. This stays visible but stops taking new applications. You can't undo a cancellation."
                          confirmLabel="Cancel opportunity"
                          onConfirm={() => setStatus("cancelled")}
                          trigger={
                            <button disabled={busyAction} data-testid="collaboration-detail-cancel-btn"
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 font-mont text-[11px] font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                              <Ban className="h-3 w-3" /> Cancel
                            </button>
                          }
                        />
                      )}
                      <ConfirmDialog
                        testIdPrefix="collaboration-detail-delete"
                        title="Delete this collaboration?"
                        description="This removes the post from The Floor and cannot be undone."
                        confirmLabel="Delete"
                        onConfirm={deleteOpportunity}
                        trigger={
                          <button disabled={busyAction || deleting} data-testid="collaboration-detail-delete-btn"
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 font-mont text-[11px] font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        }
                      />
                    </div>
                  </div>
                  {opp.kind === "njm" && adminBudget && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="collaboration-detail-budget-block">
                      <div className="rounded-lg border border-black/10 bg-paper p-2.5">
                        <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-faint">Total Budget</div>
                        <div className="font-mont text-[13px] font-bold text-ink" data-testid="collaboration-detail-budget-total">{formatBudget(adminBudget.budget.total_talent_budget)}</div>
                      </div>
                      <div className="rounded-lg border border-black/10 bg-paper p-2.5">
                        <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-faint">Planned</div>
                        <div className="font-mont text-[13px] font-bold text-ink" data-testid="collaboration-detail-budget-planned">{formatBudget(adminBudget.budget.planned)}</div>
                      </div>
                      <div className="rounded-lg border border-black/10 bg-paper p-2.5">
                        <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-faint">Committed</div>
                        <div className="font-mont text-[13px] font-bold text-ink" data-testid="collaboration-detail-budget-committed">{formatBudget(adminBudget.budget.committed)}</div>
                      </div>
                      <div className="rounded-lg border border-black/10 bg-paper p-2.5">
                        <div className="font-mont text-[9px] font-extrabold uppercase tracking-wider text-faint">Remaining</div>
                        <div className="font-mont text-[13px] font-bold text-ink" data-testid="collaboration-detail-budget-remaining">{formatBudget(adminBudget.budget.remaining)}</div>
                      </div>
                      {adminBudget.project_id && (
                        <div className="col-span-2 sm:col-span-4 font-mont text-[10.5px] text-faint" data-testid="collaboration-detail-linked-project">
                          Linked project: <span className="font-semibold text-ink">{adminBudget.project_name || adminBudget.project_id}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {!showApplications ? (
                    <button onClick={() => setShowApplications(true)} data-testid="collaboration-detail-show-applications-btn" className="font-mont text-[11px] font-bold text-brand-dim hover:underline">
                      Review applications →
                    </button>
                  ) : (
                    <CollaborationApplicationsPanel opportunityId={opp.id} roleSlots={opp.role_slots} compensation={opp.compensation} kind={opp.kind} groupByRole={opp.kind === "njm"} onDecided={() => { load(); onChanged?.(); }} />
                  )}
                </div>
              )}

              <div className="pt-1">
                <ActivityTrail endpoint={`/v1/collaboration/opportunities/${opp.id}/activity`} testIdPrefix="collaboration-detail" />
              </div>
            </div>

            <div className="shrink-0 border-t border-black/8 bg-surface2/50 px-6 sm:px-7 py-3 font-mont text-[10.5px] leading-relaxed text-faint" data-testid="collaboration-detail-footer-note">
              {opp.kind === "njm" ? "An NJM Collaboration — staff-run and paid, each role's fee (or \"Fee to be discussed\") is shown above." : showFeeDisclaimer ? DISCLAIMER_TEXT : "Peer-to-peer only — no rates or budgets, and NJM isn't involved in who gets picked."}
            </div>
          </>
        )}
      </div>

      {applySlot && (
        <ApplyToSlotModal
          opportunityId={opp.id} slotId={applySlot.slot_id} roleLabel={applySlot.role}
          compensation={opp.compensation} kind={opp.kind}
          onClose={() => setApplySlot(null)}
          onApplied={() => { setApplySlot(null); load(); onChanged?.(); }}
        />
      )}

      {showEdit && opp.kind === "njm" && (
        <CreateNjmCollaborationModal
          initialOpportunity={opp}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); onChanged?.(); }}
        />
      )}
      {showEdit && opp.kind !== "njm" && (
        <CreateCollaborationModal
          roleOptions={roleOptions} cityOptions={cityOptions}
          initialOpportunity={opp}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}
